import { Card, FormGroup, InputWithUnit, Toggle } from '../ui/Card';
import { fmt } from '../../lib/format';
import type { CalculatorInputs, CalculatorResults } from '../../lib/types';

interface Props {
  inputs: CalculatorInputs;
  results: CalculatorResults;
  updateInput: <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => void;
  collapsible?: boolean;
  summary?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function SpecialProgramsCard({ inputs, results, updateInput, collapsible, summary, isOpen, onToggle }: Props) {
  return (
    <Card title="Special Programs & 401(k)" collapsible={collapsible} summary={summary} isOpen={isOpen} onToggle={onToggle}>
      <Toggle
        label="Use 401(k) Loan for Down Payment"
        checked={inputs.use401k}
        onChange={v => updateInput('use401k', v)}
      />
      <div className={`collapsible${inputs.use401k ? ' open' : ''}`}>
        <div className="form-row" style={{ marginTop: '0.5rem' }}>
          <FormGroup label="401(k) Loan Amount">
            <InputWithUnit
              unit="$"
              value={inputs.loan401kAmount}
              onChange={v => updateInput('loan401kAmount', v)}
              min={0}
              step={1000}
            />
          </FormGroup>
          <FormGroup label="401(k) Loan Rate %">
            <InputWithUnit
              unit="%"
              unitPosition="right"
              value={inputs.loan401kRate}
              onChange={v => updateInput('loan401kRate', v)}
              min={0}
              max={15}
              step={0.25}
            />
          </FormGroup>
        </div>
        <div className="form-row">
          <FormGroup label="Repayment Term">
            <select
              value={inputs.loan401kTerm}
              onChange={e => updateInput('loan401kTerm', parseInt(e.target.value))}
            >
              <option value={5}>5 years</option>
              <option value={4}>4 years</option>
              <option value={3}>3 years</option>
            </select>
          </FormGroup>
          <FormGroup label="Monthly Payment">
            <InputWithUnit unit="$" value={fmt(results.monthly401k)} onChange={() => {}} readOnly />
          </FormGroup>
        </div>
        <div className="note-box">
          401(k) loans are repaid to your own account with interest. No credit check required.
          Payments are typically deducted from payroll. Max $50,000 or 50% of vested balance.
        </div>
      </div>

      <Toggle
        label="MN Housing Start Up Loan"
        checked={inputs.useStartUp}
        onChange={v => updateInput('useStartUp', v)}
      />
      <div className={`collapsible${inputs.useStartUp ? ' open' : ''}`}>
        <div className="form-row" style={{ marginTop: '0.5rem' }}>
          <FormGroup label="Start Up Loan Amount" hint="Up to $17,000">
            <InputWithUnit
              unit="$"
              value={inputs.startUpLoanAmt}
              onChange={v => updateInput('startUpLoanAmt', v)}
              min={0}
              max={17000}
              step={500}
            />
          </FormGroup>
          <FormGroup label="Monthly Payment">
            <InputWithUnit unit="$" value={fmt(results.monthlyStartUp)} onChange={() => {}} readOnly />
          </FormGroup>
        </div>
        <div className="note-box">
          MN Housing Start Up program: 0% interest, no monthly payments for 10 years. Deferred payment.
          Must be first-time buyer and complete homebuyer education.
        </div>
      </div>

      <Toggle
        label="Mortgage Credit Certificate (MCC)"
        checked={inputs.useMCC}
        onChange={v => updateInput('useMCC', v)}
      />
      <div className={`collapsible${inputs.useMCC ? ' open' : ''}`}>
        <div className="form-row" style={{ marginTop: '0.5rem' }}>
          <FormGroup label="Annual Tax Credit">
            <InputWithUnit
              unit="$"
              value={fmt(results.mccAnnualCredit)}
              onChange={() => {}}
              readOnly
            />
          </FormGroup>
          <FormGroup label="Monthly Savings">
            <InputWithUnit
              unit="$"
              value={fmt(results.mccMonthly)}
              onChange={() => {}}
              readOnly
            />
          </FormGroup>
        </div>
        <div className="note-box">
          MCC provides a federal tax credit of 35% of mortgage interest paid (max $2,000/yr).
          Can be combined with Start Up program. Must be first-time buyer.
        </div>
      </div>
    </Card>
  );
}
